/* eslint-disable */
import { useEffect, useState } from 'react';
import { useGetBFAwardsLeaderboardQuery } from 'common/store/modules/api/cfs-api';

const FIFTEEN_MINUTES_IN_MS =
    PULSE.app.common.CONSTANTS.TIME.ONE_MINUTE_IN_MS * 15;
const THIRTY_MINUTES_IN_MS = FIFTEEN_MINUTES_IN_MS * 2;

/**
 * AFLP-8924
 * Summary of the mental model:
 * 1. Fetch the leaderboard data.
 * 2. Get the status of the leaderboard.
 *  a. if COMPLETE, break
 *  b. if UPCOMING, go to 3.
 *  c. if LIVE go to 4a
 * 3. Check the start date.
 *  a. if less than 5 minutes, go to 4a
 *  b. else if less than 15 minutes, go to 4b
 *  c. else if less than 1h, go to 4c
 *  d. else if less than 24h go to 4d
 *  3. else go to 4e
 * 4. Go to 1.  in:
 *  a. 30s
 *  b. 1m
 *  c. 5min
 *  d. 10 minutes
 *  e. stop
 *
 * @param {string} timestamp Date string that can be parsed by Date()
 * @returns {number} Number of milliseconds to the next fetch
 */
const getPollingInterval = (timestamp) => {
    try {
        const destinationTime = new Date(timestamp).getTime();
        const nowTime = Date.now();

        if (!isNaN(destinationTime)) {
            const timeDiff = destinationTime - nowTime;

            if (destinationTime === 0) {
                // Destination time is not set. Do not poll
                return 0;
            } else if (
                destinationTime < 0 ||
                timeDiff <= PULSE.app.common.CONSTANTS.TIME.FIVE_MINS_IN_MS
            ) {
                // Less than 5 minutes to date or the date already passed, poll every 30 seconds
                return PULSE.app.common.CONSTANTS.TIME.THIRTY_SECS_IN_MS;
            } else if (timeDiff <= FIFTEEN_MINUTES_IN_MS) {
                // Less than 15 minutes to date, poll every 1 minute
                return PULSE.app.common.CONSTANTS.TIME.ONE_MINUTE_IN_MS;
            } else if (
                timeDiff <= PULSE.app.common.CONSTANTS.TIME.ONE_HOUR_IN_MS
            ) {
                // Less than 1 hour to date, poll every 5 minutes
                return PULSE.app.common.CONSTANTS.TIME.FIVE_MINS_IN_MS;
            } else if (
                timeDiff <= PULSE.app.common.CONSTANTS.TIME.ONE_DAY_IN_MS
            ) {
                // Less than 1 day to date, poll every 30 minutes
                return THIRTY_MINUTES_IN_MS;
            } else {
                // Mor than 1 day to date, no polling
                return 0;
            }
        }
    } catch (error) {
        console.error(error);
    }

    // default poll every 30 seconds
    return PULSE.app.common.CONSTANTS.TIME.THIRTY_SECS_IN_MS;
};

/**
 * If there is a live season we should call the refetchLeaderBoardData in every pollingDelay interval
 *
 * @param leaderboardData - the current leaderboard data stored as an object - object
 * @param refetchLeaderboardData - a function to refetch the leaderboard data - function
 * @param seasonPid
 * @param pollingDelay - the delay between each poll for new leaderboard data - number
 * @param eventStartDateTime - the date time at which the event starts (the closer the current time is to this date, the more frequent the polling will be)
 */
export function useLeaderboardRefetch(
    seasonPid,
    pollingDelay = PULSE.app.common.CONSTANTS.TIME.THIRTY_SECS_IN_MS,
    eventStartDateTime = '1970-01-01T00:00:00Z'
) {
    const [pollingInterval, setPollingInterval] = useState(0);

    const { data: leaderboardData, isFetching } =
        useGetBFAwardsLeaderboardQuery(
            {
                seasonId: seasonPid
            },
            {
                skip: !(seasonPid && seasonPid !== ''),
                pollingInterval: pollingInterval
            }
        );

    useEffect(() => {
        if (leaderboardData && !isFetching) {
            const leaderboardStatus = PULSE.app.common.match.getMatchStatus(
                leaderboardData?.status
            );

            if (
                leaderboardStatus ===
                PULSE.app.common.CONSTANTS.MATCH_STATUS.COMPLETED
            ) {
                console.info(
                    'Player Awards Leaderboard - Status COMPLETED - STOP polling'
                );
                setPollingInterval(0);
            } else if (
                leaderboardStatus ===
                    PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING ||
                leaderboardData?.status === null
            ) {
                console.info(
                    'Player Awards Leaderboard - Status UPCOMING / NULL'
                );
                const interval = getPollingInterval(eventStartDateTime);
                console.info(
                    `Player Awards Leaderboard - Next fetch in: ${interval}ms`
                );
                setPollingInterval(interval);
            } else if (
                leaderboardStatus ===
                PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE
            ) {
                console.info(
                    `Player Awards Leaderboard - Next fetch in: ${pollingDelay}`
                );
                setPollingInterval(pollingDelay);
            }
        }
    }, [isFetching, leaderboardData]);
}
